import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, HeartHandshake, ArrowUpRight, RefreshCw, Compass, ChevronRight, Gift } from 'lucide-react';
import { Reveal, EASE } from './helpers';

const TRANSITION_TONES = [
    {
        key: 'resignation',
        icon: LogOut,
        label: 'Resignation',
        lede: "They've handed in their notice. The clock starts.",
        sampleHeadline: 'Your work, recognised.',
        sample: 'Over four years and 142 captured decisions, you built the renewal process this company runs on. We hand you a record of the customers you held, the playbooks you wrote, and the principles you leave behind.',
    },
    {
        key: 'layoff',
        icon: HeartHandshake,
        label: 'Layoff or restructure',
        lede: 'Dignity mode. Switched on by default.',
        sampleHeadline: 'What you did here matters.',
        sample: "This isn't framed as an ending. The tone of every document is about what you built, what you decided, what you knew — never about performance or fit. You walk out with your work, intact and yours.",
        featured: true,
    },
    {
        key: 'promotion',
        icon: ArrowUpRight,
        label: 'Promotion',
        lede: 'Hand the old role over. Step into the new one.',
        sampleHeadline: 'The work that earned the next role.',
        sample: 'Your notes become the brief you hand to your successor — and the evidence of how you think for the next chapter. The cleanest version of your work the company has ever seen.',
    },
    {
        key: 'internal',
        icon: RefreshCw,
        label: 'Internal move',
        lede: 'Continuity for the team you leave behind.',
        sampleHeadline: 'Two things travel with you.',
        sample: 'The decision notes stay with the role; your personal profile and principles travel with you into the next team. Same person, new context — the record comes too.',
    },
    {
        key: 'shift',
        icon: Compass,
        label: 'Career change',
        lede: 'Take what you know with you, however far you go.',
        sampleHeadline: "What's portable about you.",
        sample: "A summary of what you're well-suited for next — the environments that fit, the roles you're ready for, what makes you distinctive across very different fields.",
    },
];

export const TransitionTypes = () => {
    const [active, setActive] = useState('layoff');
    const current = TRANSITION_TONES.find((t) => t.key === active) ?? TRANSITION_TONES[0];

    return (
        <section id="moments" className="py-32 scroll-mt-16 border-t border-border/40 bg-muted/20">
            <div className="max-w-6xl mx-auto px-6">
                <Reveal className="max-w-2xl mb-12">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">Every kind of departure</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                        A layoff isn't a promotion.
                        <span className="text-muted-foreground/60"> The tone shouldn't be the same.</span>
                    </h2>
                    <p className="mt-5 text-lg text-muted-foreground leading-relaxed">Pick a type of departure — watch the document the person receives change to match.</p>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5 flex flex-col gap-2">
                        {TRANSITION_TONES.map((t) => {
                            const isActive = t.key === active;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setActive(t.key)}
                                    aria-pressed={isActive}
                                    aria-label={`Show preview for ${t.label}`}
                                    className={`text-left rounded-2xl p-5 transition-all flex items-start gap-4 border ${
                                        isActive ? 'bg-card border-primary/40 shadow-sm' : 'bg-card/40 border-border/40 hover:bg-card/80 hover:border-border'
                                    }`}
                                >
                                    <div
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                            isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                                        }`}
                                    >
                                        <t.icon className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold">{t.label}</span>
                                            {t.featured && <span className="text-[9px] uppercase tracking-widest font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">Default on</span>}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{t.lede}</div>
                                    </div>
                                    <motion.div animate={{ opacity: isActive ? 1 : 0, x: isActive ? 0 : -4 }} transition={{ duration: 0.25 }}>
                                        <ChevronRight className="h-4 w-4 text-primary mt-3" />
                                    </motion.div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="lg:col-span-7">
                        <div className="rounded-2xl overflow-hidden border border-border/60 bg-gradient-to-br from-card to-muted/40 min-h-[420px] flex flex-col">
                            <div className="px-7 pt-7 pb-4 flex items-center justify-between border-b border-border/40 bg-background/40">
                                <div className="flex items-center gap-2">
                                    <Gift className="h-4 w-4 text-primary" />
                                    <span className="text-xs font-semibold uppercase tracking-widest">What the person receives</span>
                                </div>
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={current.key}
                                        initial={{ opacity: 0, x: 8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -8 }}
                                        transition={{ duration: 0.3 }}
                                        className="text-[10px] font-medium px-2 py-0.5 rounded bg-muted/60 text-muted-foreground"
                                    >
                                        {current.label}
                                    </motion.span>
                                </AnimatePresence>
                            </div>

                            <div className="px-8 py-10 flex-1 flex flex-col justify-center">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={current.key}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        transition={{ duration: 0.5, ease: EASE }}
                                    >
                                        <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5 leading-tight">{current.sampleHeadline}</h3>
                                        <p className="text-base text-foreground/80 leading-relaxed">{current.sample}</p>

                                        {current.key === 'layoff' && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.3, duration: 0.5 }}
                                                className="mt-6 flex items-start gap-2 text-xs text-primary bg-primary/5 rounded-lg p-3 border border-primary/15"
                                            >
                                                <HeartHandshake className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <span className="font-semibold">What dignity mode actually does:</span>{' '}
                                                    <span className="text-foreground/70">no ending language. No performance framing. No pity. Just their work, recognised.</span>
                                                </div>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
